import { useCallback, useEffect, useState } from "react";
import { OrganizationProfile } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Star, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";

const roleLabel = { "org:admin": "Owner / Admin", "org:editor": "Editor / QA", "org:member": "Reviewer" };

export default function Team() {
  const api = useApi();
  const { isAdmin } = useRole();
  const [members, setMembers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [switchingRole, setSwitchingRole] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([api.get("/members"), api.get("/staff/leads")]);
      setMembers(m.data.filter((x) => x.user_id));
      setLeads(l.data.lead_user_ids || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLead = async (userId) => {
    if (!isAdmin) return;
    const next = leads.includes(userId) ? leads.filter((x) => x !== userId) : [...leads, userId];
    setLeads(next);
    setSaving(true);
    try { await api.put("/staff/leads", { lead_user_ids: next }); toast.success("Leads updated"); }
    catch (e) { toast.error("Failed to update leads"); load(); }
    finally { setSaving(false); }
  };

  const setAppRole = async (member, appRole) => {
    setSwitchingRole(member.user_id);
    try {
      await api.put(`/members/${member.user_id}/app-role`, { app_role: appRole });
      toast.success(appRole === "editor" ? `${member.name} promoted to Editor` : `${member.name} set back to Reviewer`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update role"); }
    finally { setSwitchingRole(null); }
  };

  return (
    <div className="space-y-8" data-testid="team-container">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Team</h1>
        <p className="text-[#7A9090]">Manage members, roles, and team leads.</p>
      </div>

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={20} weight="fill" className="text-[#5FA8B4]" />
          <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Roles</h2>
        </div>
        <p className="text-[#7A9090] text-sm mb-4">
          Clerk only offers Admin/Member on this plan, so <strong>Editor</strong> is an app-level
          promotion — promote a Member here to let them create content, manage media and
          approve/decline posts inside Post Your Content. Members left as <strong>Reviewer</strong>{" "}
          can only leave feedback/suggestions.
        </p>
        {loading ? (
          <p className="text-[#7A9090]">Loading members…</p>
        ) : (
          <div className="space-y-2">
            {members.map((m) => (
              <div key={`role-${m.user_id}`} data-testid={`role-row-${m.user_id}`} className="flex items-center justify-between gap-3 p-3 rounded-[10px] border border-[#eef1f2] overflow-hidden">
                <div className="min-w-0 flex-1">
                  <p className="text-[#3C4048] text-sm font-semibold truncate">{m.name}</p>
                  {m.name !== m.email && <p className="text-[#7A9090] text-xs truncate">{m.email}</p>}
                </div>
                {m.role === "org:member" ? (
                  <select
                    data-testid={`role-select-${m.user_id}`}
                    disabled={!isAdmin || switchingRole === m.user_id}
                    value={m.effective_role === "org:editor" ? "editor" : "member"}
                    onChange={(e) => setAppRole(m, e.target.value)}
                    className="text-xs border border-[#e5e7eb] rounded-[8px] px-2 py-1.5 text-[#3C4048] bg-white shrink-0 disabled:opacity-50"
                  >
                    <option value="member">Reviewer (feedback only)</option>
                    <option value="editor">Editor (Post Your Content)</option>
                  </select>
                ) : (
                  <span className="text-xs text-[#7A9090] font-semibold shrink-0">{roleLabel[m.role] || m.role}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Star size={20} weight="fill" className="text-[#5FA8B4]" />
          <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Team Leads</h2>
        </div>
        <p className="text-[#7A9090] text-sm mb-4">Leads see <strong>all</strong> posts (company + every assigned post). Regular staff only see company posts and posts assigned to them.</p>
        {loading ? (
          <p className="text-[#7A9090]">Loading members…</p>
        ) : members.length === 0 ? (
          <p className="text-[#7A9090]">No members found.</p>
        ) : (
          <div className="space-y-2">
            {members.map((m) => {
              const isLead = leads.includes(m.user_id);
              return (
                <div key={m.user_id} data-testid={`member-${m.user_id}`} className="flex items-center justify-between gap-3 p-3 rounded-[10px] border border-[#eef1f2] overflow-hidden">
                  <div className="min-w-0 flex-1">
                    <p className="text-[#3C4048] text-sm font-semibold truncate">{m.name}</p>
                    <p className="text-[#7A9090] text-xs truncate">{m.name !== m.email ? `${m.email} · ` : ""}{roleLabel[m.effective_role] || roleLabel[m.role] || m.role}</p>
                  </div>
                  <Button
                    data-testid={`lead-toggle-${m.user_id}`}
                    disabled={!isAdmin || saving || m.effective_role !== "org:member"}
                    onClick={() => toggleLead(m.user_id)}
                    variant={isLead ? "default" : "outline"}
                    size="sm"
                    className={`rounded-full gap-1 shrink-0 ${isLead ? "bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white" : "border-[#e5e7eb] text-[#3C4048]"}`}
                    title={m.effective_role !== "org:member" ? "Only Reviewers can be leads" : ""}
                  >
                    {isLead ? <><CheckCircle size={14} weight="fill" /> Lead</> : "Make lead"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-bold text-[#3C4048] mb-3" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Members & Invitations</h2>
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] overflow-x-auto">
          <OrganizationProfile routing="hash" />
        </div>
      </div>
    </div>
  );
}
